import { Skeleton } from '@affine/component';
import {
  SettingHeader,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useQuery } from '@affine/core/components/hooks/use-query';
import { SubscriptionService } from '@affine/core/modules/cloud';
import { tariffsQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect } from 'react';

import type { SettingState } from '../../types';
import { AIPlanCard } from './ai-plan-card';
import { BelieverIdentifier } from './biliever-identifier';
import { BillingHistory } from './billing-history';
import { PaymentMethod } from './payment-method';
import { ProPlanCard } from './pro-plan-card';
import * as styles from './style.css';
import { TariffPlanCard } from './tariff-plan-card';
import { TypeformLink } from './typeform-link';

// Fork billing: yookassa-managed subscriptions have no stripe customer portal.
const isYookassaSubscription = (
  subscription: { provider?: string | null } | null | undefined
) => subscription?.provider === 'yookassa';

export const BillingSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const t = useI18n();

  return (
    <>
      <SettingHeader
        title={t['com.affine.payment.billing-setting.title']()}
        subtitle={t['com.affine.payment.billing-setting.subtitle']()}
      />
      <SettingWrapper
        title={t['com.affine.payment.billing-setting.information']()}
      >
        <SubscriptionSettings onChangeSettingState={onChangeSettingState} />
      </SettingWrapper>
      <SettingWrapper title={t['com.affine.payment.billing-setting.history']()}>
        <BillingHistory />
      </SettingWrapper>
    </>
  );
};

const SubscriptionSettings = ({
  onChangeSettingState,
}: {
  onChangeSettingState: (state: SettingState) => void;
}) => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    subscriptionService.prices.revalidate();
  }, [subscriptionService]);
  // Fork billing: yookassa tariffs replace the stock stripe billing cards
  const { data: tariffsData } = useQuery(
    { query: tariffsQuery },
    { suspense: false }
  );
  const tariffs = tariffsData?.tariffs;

  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const isBeliever = useLiveData(subscriptionService.subscription.isBeliever$);
  const yookassaMode = !!tariffs || isYookassaSubscription(proSubscription);

  const openPlans = useCallback(
    (scrollAnchor?: string) => {
      onChangeSettingState({
        activeTab: 'plans',
        scrollAnchor: scrollAnchor,
      });
    },
    [onChangeSettingState]
  );
  const gotoCloudPlansSetting = useCallback(
    () => openPlans('cloudPricingPlan'),
    [openPlans]
  );
  const gotoAiPlanSetting = useCallback(
    () => openPlans('aiPricingPlan'),
    [openPlans]
  );

  if (tariffs) {
    return (
      <div className={styles.subscription}>
        {proSubscription !== null ? (
          <TariffPlanCard
            tariffs={tariffs}
            gotoCloudPlansSetting={gotoCloudPlansSetting}
          />
        ) : (
          <SubscriptionSettingSkeleton />
        )}
        {proSubscription ? (
          <p className={styles.yookassaNote}>
            {t['com.affine.payment.yookassa.payment-method-note']()}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.subscription}>
      {yookassaMode ? null : <AIPlanCard onClick={gotoAiPlanSetting} />}

      {proSubscription !== null ? (
        isBeliever ? (
          <BelieverIdentifier onOpenPlans={gotoCloudPlansSetting} />
        ) : (
          <ProPlanCard gotoCloudPlansSetting={gotoCloudPlansSetting} />
        )
      ) : (
        <SubscriptionSettingSkeleton />
      )}

      {yookassaMode ? null : <TypeformLink />}

      {proSubscription !== null ? (
        yookassaMode ? (
          <p className={styles.yookassaNote}>
            {t['com.affine.payment.yookassa.payment-method-note']()}
          </p>
        ) : (
          proSubscription && <PaymentMethod />
        )
      ) : (
        <SubscriptionSettingSkeleton />
      )}
    </div>
  );
};

const SubscriptionSettingSkeleton = () => {
  const t = useI18n();
  return (
    <SettingWrapper
      title={t['com.affine.payment.billing-setting.information']()}
    >
      <div className={styles.subscriptionSettingSkeleton}>
        <Skeleton variant="rounded" height="104px" />
        <Skeleton variant="rounded" height="46px" />
      </div>
    </SettingWrapper>
  );
};
